import { isNil } from 'ramda';
import React, { ReactNode } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { supportedLanguages } from '~admin/i18n';
import { Language } from '~common/i18n';
import { getTranslationInitialValues } from '~admin/view/components/create-event/helper/create-event-initial-values.helper';
import { Box } from '~common/components/box.component';
import { Button } from '~common/components/button/button.component';
import { Size } from '~common/components/button/button.helper';
import { Column } from '~common/components/column.component';
import { Color } from '~common/model/basics';
import { PlusIcon } from '~web/view/components/ui/icon/plus.icon';
import { isNilOrEmpty } from '~common/helper/global';

interface FieldProps {
  name: string;
  label: string;
}

interface TranslationFieldProps {
  name: string;
  label?: string;
  render: (props: FieldProps) => ReactNode;
}

export interface TranslationFormData<T> {
  language: Language;
  value: T;
}

export const TranslationField: React.FunctionComponent<TranslationFieldProps> = ({
  name,
  label,
  render,
}) => {
  const { t } = useTranslation();

  return (
    <Container>
      <FieldArray name={name}>
        {({ fields }): ReactNode => {
          const disableAddButton =
            fields.length === supportedLanguages.length ||
            isNilOrEmpty(fields.value) ||
            isNil(fields.value[0]?.value);

          const onRemove = (index: number): void => {
            fields.remove(index);
          };

          const onAdd = (): void => {
            supportedLanguages.forEach((language: Language) => {
              const languageNotAdded = !fields.value.find(
                (field: TranslationFormData<any>) => field.language === language,
              );
              if (languageNotAdded) {
                fields.push(getTranslationInitialValues(language));
              }
            });
          };

          return (
            <>
              <Fields>
                {fields.map((name: string, index: number): ReactNode | null => (
                  <Column key={index}>
                    {render({
                      name: `${name}.value`,
                      label: `${label} - ${fields.value[index].language}`,
                    })}
                    {index >= 1 && (
                      <RemoveButtonContainer>
                        <Button
                          color={Color.Gray}
                          size={Size.Small}
                          onClick={(): void => onRemove(index)}
                        >
                          {t('general.removeTranslation')}
                        </Button>
                      </RemoveButtonContainer>
                    )}
                  </Column>
                ))}
              </Fields>
              <AddButtonContainer>
                <Button
                  color={Color.Gray}
                  size={Size.Small}
                  disabled={disableAddButton}
                  onClick={onAdd}
                >
                  <StyledPlusIcon width={12} height={12} />
                  {t('general.addTranslation')}
                </Button>
              </AddButtonContainer>
            </>
          );
        }}
      </FieldArray>
    </Container>
  );
};

const Container = styled(Column)`
  width: 100%;
`;

const Fields = styled(Column)`
  > div:not(:first-child) {
    margin-top: 24px;
  }
`;

const RemoveButtonContainer = styled(Box)`
  justify-content: flex-start;
`;

const AddButtonContainer = styled(Box)`
  margin-top: 16px;
  justify-content: flex-end;
`;

const StyledPlusIcon = styled(PlusIcon)`
  fill: currentColor;
  margin-right: 4px;
`;



// WEBPACK FOOTER //
// ./view/components/ui/form/fields/field-translation.component.tsx